import { animationFrameId, documentWithAnimationAtTime } from "@/document/animation/applyAnimation";
import { findSceneNode, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { createId } from "@/lib/ids";

const REGROUP_FRAGMENT_PATTERN = /^(.+):regroup:(words|symbols|symbolsWithoutWhitespace):/;

export function isRegroupFragmentId(nodeId: string): boolean {
  return REGROUP_FRAGMENT_PATTERN.test(nodeId);
}

export function regroupSourceIdFromFragment(nodeId: string): string | undefined {
  return nodeId.match(REGROUP_FRAGMENT_PATTERN)?.[1];
}

export function prepareBakedFrameChildren(
  document: CurvaDocument,
  frameId: string,
  timeMs: number,
  rootNodeId?: string,
): SceneNode[] {
  const evaluation = documentWithAnimationAtTime(document, timeMs, rootNodeId);
  const animatedFrame = findSceneNode(evaluation.document, frameId);
  if (animatedFrame?.type !== "frame") {
    throw new Error(`Frame not found: ${frameId}`);
  }

  const regroupedSourceIds = collectRegroupedSourceIds(animatedFrame.children);
  return bakeChildren(document, animatedFrame.children, regroupedSourceIds);
}

export function sceneNodeTreeIds(nodes: SceneNode[]): string[] {
  const ids: string[] = [];
  walkSceneNodes(nodes, node => {
    ids.push(node.id);
  });
  return ids;
}

function collectRegroupedSourceIds(nodes: SceneNode[]): Set<string> {
  const sourceIds = new Set<string>();
  walkSceneNodes(nodes, node => {
    const sourceId = regroupSourceIdFromFragment(node.id);
    if (sourceId) {
      sourceIds.add(sourceId);
    }
  });
  return sourceIds;
}

function bakeChildren(
  sourceDocument: CurvaDocument,
  animatedChildren: SceneNode[],
  regroupedSourceIds: Set<string>,
): SceneNode[] {
  return animatedChildren
    .filter(child => !regroupedSourceIds.has(child.id))
    .map(child => bakeNode(sourceDocument, child, regroupedSourceIds));
}

function bakeNode(
  sourceDocument: CurvaDocument,
  animatedNode: SceneNode,
  regroupedSourceIds: Set<string>,
): SceneNode {
  const baked = structuredClone(animatedNode);

  if (isRegroupFragmentId(baked.id)) {
    baked.id = createId("text");
    if (baked.type === "text") {
      baked.visible = true;
    }
    return baked;
  }

  const original = findSceneNode(sourceDocument, baked.id);
  if (original) {
    baked.name = original.name;
    if (original.nameManuallyEdited !== undefined) {
      baked.nameManuallyEdited = original.nameManuallyEdited;
    }
  }

  const children = getSceneNodeChildren(baked);
  if (children) {
    setSceneNodeChildren(baked, bakeChildren(sourceDocument, children, regroupedSourceIds));
  }

  return baked;
}

function setSceneNodeChildren(node: SceneNode, children: SceneNode[]): void {
  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    node.children = children;
    return;
  }
  if (node.type === "boolean") {
    node.sourceNodes = children;
  }
}

function walkSceneNodes(nodes: SceneNode[], visit: (node: SceneNode) => void): void {
  for (const node of nodes) {
    visit(node);
    const children = getSceneNodeChildren(node);
    if (children) {
      walkSceneNodes(children, visit);
    }
  }
}

export function animationBakeFrameId(document: CurvaDocument, rootNodeId?: string): string | undefined {
  return animationFrameId(document.animation, rootNodeId);
}
